import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Dropdown } from 'primereact/dropdown'
import FilterBar, { type FilterTab } from '../../../components/ui/FilterBar'
import type { TicketLookups } from '../types'

export interface TicketFilterValue {
  statusId: number | null
  officeId: number | null
  typeId: number | null
  q: string
}

export interface TicketCounts {
  all: number
  byStatus: Record<number, number>
}

interface TicketFiltersProps {
  lookups?: TicketLookups
  counts: TicketCounts
  value: TicketFilterValue
  onChange: (patch: Partial<TicketFilterValue>) => void
  /** Tüm filtre + aramayı tek adımda başlangıç durumuna döndürür. */
  onClear: () => void
  /** Ana aramanın hemen sağına çizilir (tam PNR/Ad-Soyad araması) — bkz. FilterBar.searchExtra. */
  searchExtra?: ReactNode
}

/**
 * Bilet listesi filtre çubuğu — paylaşılan ui/FilterBar kabuğu (durum sekmeleri + arama + temizle)
 * üzerine bilet-özel kontroller (Makam radio grubu + Tür dropdown) slot olarak (013 migrasyon; ham
 * select/radio → PrimeReact Dropdown/RadioButton). Davranış/props korunur; tüm filtreler VE ile birleşir.
 */
export default function TicketFilters({
  lookups,
  counts,
  value,
  onChange,
  onClear,
  searchExtra,
}: Readonly<TicketFiltersProps>) {
  const { t } = useTranslation()

  const hasActiveFilter =
    value.statusId != null ||
    value.officeId != null ||
    value.typeId != null ||
    value.q.trim() !== ''

  const tabs: FilterTab[] = [
    { id: null, label: t('tickets.filters.all'), count: counts.all },
    ...(lookups?.statuses ?? []).map((s) => ({
      id: s.id,
      label: s.name,
      count: counts.byStatus[s.id] ?? 0,
    })),
  ]

  const officeOptions = [
    { label: t('tickets.filters.officeAll'), value: null },
    ...(lookups?.offices ?? []).map((o) => ({ label: o.name, value: o.id })),
  ]

  const typeOptions = [
    { label: t('tickets.filters.all'), value: null },
    ...(lookups?.types ?? []).map((ty) => ({ label: ty.name, value: ty.id })),
  ]

  return (
    <FilterBar
      tabs={tabs}
      activeTabId={value.statusId}
      onTab={(id) => onChange({ statusId: id })}
      searchValue={value.q}
      searchPlaceholder={t('tickets.filters.search')}
      searchAriaLabel={t('tickets.filters.search')}
      onSearch={(q) => onChange({ q })}
      showClear={hasActiveFilter}
      clearLabel={t('tickets.filters.clear')}
      onClear={onClear}
      searchExtra={searchExtra}
    >
      {/* Figma sırası: Makam Türü → Talep Türü. */}
      <Dropdown
        className="tk-filter"
        aria-label={t('tickets.filters.office')}
        value={value.officeId}
        options={officeOptions}
        placeholder={`${t('tickets.filters.office')}: ${t('tickets.filters.all')}`}
        valueTemplate={(opt: { label: string } | null) =>
          `${t('tickets.filters.office')}: ${opt ? opt.label : t('tickets.filters.all')}`
        }
        onChange={(e) => onChange({ officeId: e.value as number | null })}
      />

      <Dropdown
        className="tk-filter"
        panelClassName="tk-type-panel"
        aria-label={t('tickets.filters.type')}
        value={value.typeId}
        options={typeOptions}
        placeholder={`${t('tickets.filters.type')}: ${t('tickets.filters.all')}`}
        valueTemplate={(opt: { label: string } | null) =>
          `${t('tickets.filters.type')}: ${opt ? opt.label : t('tickets.filters.all')}`
        }
        onChange={(e) => onChange({ typeId: e.value as number | null })}
      />
    </FilterBar>
  )
}
